import { format } from 'date-fns';
import delve from 'dlv';

import { ICartEntry, IChannelExclusions, IOffer, ISection, MenuObject } from '@rbi-ctg/menu';
import { IEvaluationFeedback, IRedemptionEligibility } from '@rbi-ctg/offers';
import { MenuObjectTypes } from 'enums/menu';
import { CartOfferState, IOfferFeedbackEntryFragment } from 'generated/rbi-graphql';

import { isKiosk, isNative, isWeb, platform } from '../environment';

import { OfferPlatform } from './rule-sets/types';

export enum OfferActivationStatus {
  CAN_ACTIVATE = 'CAN_ACTIVATE',
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  ACTIVATED = 'ACTIVATED'
}

export const activationStatusFromOfferState = (offerState: CartOfferState | null) => {
  switch (offerState) {
    case CartOfferState.ACTIVE:
      return OfferActivationStatus.ACTIVATED;
    default:
      return OfferActivationStatus.CAN_ACTIVATE;
  }
};

/**
 * Resolves to highest level menuObject of type
 * 'Combo' or 'Item' for a menuObject tree.
 */
export const flattenMenuObject = (menuObject: MenuObject): string[] => {
  // Root case, we've met first Item/Combo in menuObject tree.
  if (menuObject._type === 'combo' || menuObject._type === 'item') {
    return [menuObject._id];
  }

  // This is a little complicated, but recursively calls the
  // children and flattens returned arrays from callstack.
  if (menuObject.options) {
    // @ts-ignore this cast is probably not strictly correct but I could not get
    // typescript to resolve the correct call signature without refining to a more specific type
    return (menuObject as ISection).options.reduce<string[]>(
      (accum: string[], childMenuObject: MenuObject): string[] => {
        const childObject: MenuObject = (childMenuObject as any).option || childMenuObject;
        const flattenedChild: string[] = flattenMenuObject(childObject);
        return accum.concat(flattenedChild);
      },
      []
    );
  }

  return [];
};

/**
 * Reduces requirements and cartEntries to necessary id sets and
 * returns true if they share an id.
 * @deprecated
 */
export const verifyPurchase = (purchaseRequirement: MenuObject, cartEntries: ICartEntry[]) => {
  const requiredIds: string[] = flattenMenuObject(purchaseRequirement);

  return cartEntries.some(cartEntry => requiredIds.includes(cartEntry._id));
};

/**
 * @deprecated
 */
export const verifyPlatform = (couponPlatform: OfferPlatform): boolean => {
  return platform().toLowerCase() === couponPlatform.toLowerCase();
};

/**
 * @deprecated
 */
export const channelExclusion = (isDelivery: boolean) => (offer: IOffer) => {
  const exclusions: IChannelExclusions = delve(offer, 'channelExclusions') || {};

  if (exclusions.web) {
    return !isWeb;
  }
  if (exclusions.kiosk) {
    return !isKiosk;
  }
  if (exclusions.mobile) {
    return !isNative;
  }
  if (exclusions.delivery) {
    return !isDelivery;
  }
  return true;
};

export const getOfferFeedbackErrors = (offer: IRedemptionEligibility) => {
  return delve(offer, 'evaluationFeedback', []).map(
    (evaluationFeedback: IEvaluationFeedback) =>
      `#${evaluationFeedback.code} - ${evaluationFeedback.message}`
  );
};

export const reduceOffersFeedback = (
  offersFeedback: ReadonlyArray<IOfferFeedbackEntryFragment>
): Record<string, IOfferFeedbackEntryFragment> =>
  offersFeedback.reduce(
    (acc, entry) => ({
      ...acc,
      [entry.tokenId || entry.couponId]: entry
    }),
    {}
  );

export const offersFeedbacksById = (
  offersFeedback: ReadonlyArray<IOfferFeedbackEntryFragment>
): Record<string, IOfferFeedbackEntryFragment[] | undefined> =>
  offersFeedback.reduce((acc, entry) => {
    const existingFeedbacks = acc[entry._id] || [];
    return { ...acc, [entry._id]: [...existingFeedbacks, entry] };
  }, {});

export function getAnonEndingDate() {
  return new Date().setDate(new Date().getDate() + 1);
}

export const makeUrlForOffer = (offer: IOffer) => {
  if (!offer.option || !offer.vendorConfigs) {
    return null;
  }
  const {
    option: { _id, _type },
    vendorConfigs
  } = offer;

  if (_type === MenuObjectTypes.OFFER_DISCOUNT) {
    return null;
  }

  const urlOfferPluParams = Object.keys(vendorConfigs).reduce((urlParams, vendor) => {
    urlParams.append(`offer`, `${vendor}-${JSON.stringify(vendorConfigs[vendor])}`);
    return urlParams;
  }, new URLSearchParams(''));

  return `/menu/${_type}-${_id}?${urlOfferPluParams}`;
};

export const isMoreInfoDisclaimerAvailable = (offer: IOffer): boolean => {
  const moreInfoText = offer?.moreInfo?.localeRaw?.[0]?.children?.[0]?.text;
  return !!moreInfoText;
};

export const redeemedOnTime = (): string => format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");

// ruleset utils
export * from './rule-sets';
